import { waPhone } from "@/lib/phone";
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import InstallAppButton from "@/components/InstallAppButton";
import MyTasksBanner from "@/components/MyTasksBanner";
import CuttingPlatesSection from "@/components/CuttingPlatesSection";
import GateSheetSizes from "@/components/GateSheetSizes";
import { GATE_V2_STAGES } from "@/lib/gate";
import {
  Buildings, Phone, MapPin, CurrencyInr, Package, ArrowLeft, SignOut,
  CheckCircle, Circle, ImageSquare, Clock, WhatsappLogo,
} from "@phosphor-icons/react";

// Fabricator's stage view — reflects actual workshop flow:
//   NEW ORDER → FRAME READY (waiting) → CUTTING DONE (machine finished cutting sheets)
//   → DESIGN INSTALL (fabricator installs cut sheets into frame)
//   → READY FOR DELIVERY (gate assembled) → DELIVERED.
// Note: the underlying admin statuses `design_making` (admin creating design) and
// `design_cutting` (on the CNC) are collapsed into "CUTTING DONE" from the
// fabricator's POV because they don't act during those stages.
const STAGES = [
  { key: "new_order", label: "New Order" },
  { key: "structure_making", label: "Structure / Framing Making" },
  { key: "structure_ready", label: "Structure Ready" },
  { key: "sheet_size_pending", label: "Upload Sheet Size" },
  { key: "design_making", label: "Design Making" },
  { key: "size_uploaded", label: "Size Sent" },
  { key: "size_confirmed", label: "Size Confirmed" },
  { key: "design_pending", label: "Design" },
  { key: "ready_to_cut", label: "Ready to Cut" },
  { key: "in_cutting", label: "Cutting" },
  { key: "final_fitting", label: "Final Fitting" },
  { key: "ready_for_installation", label: "Ready for Installation" },
  { key: "completed", label: "Ready for Delivery" },
  { key: "delivered", label: "Delivered" },
  { key: "all", label: "All" },
];

const PLATE_STAGES = [
  { key: "new_order", label: "New Order" },
  { key: "framing_making", label: "Framing Making" },
  { key: "design_making", label: "Design Making" },
  { key: "design_cutting", label: "Design Cutting" },
  { key: "completed", label: "Ready for Delivery" },
  { key: "delivered", label: "Delivered" },
];

const STAGE_LABEL = {
  ...Object.fromEntries(STAGES.map((s) => [s.key, s.label])),
  // Legacy aliases — same bucket
  framing_making: "Structure Making",
  design_making: "Design",
  design_cutting: "Cutting",
};

/**
 * FabricationDashboard — landing page for users with role="fabrication".
 * They log in via the SAME /login page with email + password, and after
 * login the app redirects them here based on role. Shows only their own
 * assigned gate/railing orders with sizes, status timeline, and the
 * advance ledger (rates/profit hidden).
 */
export default function FabricationDashboard() {
  const { user, logout } = useAuth();
  const [openOrder, setOpenOrder] = useState(null);

  if (user === null) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 font-mono-num">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "fabrication") {
    // Sent to the right dashboard for their role
    if (user.role === "operator") return <Navigate to="/operator" replace />;
    return <Navigate to="/admin" replace />;
  }

  if (openOrder) {
    return <OrderDetail orderId={openOrder} me={user} onBack={() => setOpenOrder(null)} onLogout={logout} />;
  }
  return <OrdersList me={user} onOpen={setOpenOrder} onLogout={logout} />;
}

/* ------------------------------------------------------------------ */
function OrdersList({ me, onOpen, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [stage, setStage] = useState("new_order");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/fabrication/orders");
      setOrders(data.orders || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAction = async (order, action) => {
    const confirmMsg = action === "frame_done"
      ? `#${order.order_number} — Frame ready mark karein? Order design stage me jayega.`
      : `#${order.order_number} — Gate ready for delivery? Order DELIVERED ho jayega.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/fabrication/orders/${order.id}/status`, { action });
      toast.success(action === "frame_done" ? "Frame done ✓" : "Ready for delivery ✓ — DELIVERED");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    if (stage === "all") return orders;
    if (stage === "framing_making") {
      // Frame Ready bucket also includes admin's `design_making` since the
      // fabricator has no action to take during design-confirmation.
      return orders.filter((o) => o.status === "framing_making" || o.status === "design_making");
    }
    return orders.filter((o) => o.status === stage);
  }, [orders, stage]);

  const totalAdvance = orders.reduce((s, o) => s + (o.advance_from_shobhit || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="fabrication-dashboard">
      <header className="sticky top-0 z-10 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-orange-500/20 border border-orange-500/50 flex items-center justify-center">
            <Buildings size={18} className="text-orange-400" weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono-num tracking-widest text-orange-400">// FABRICATION DEPARTMENT</div>
            <div className="font-heading font-black truncate">{me.name}</div>
          </div>
          <InstallAppButton compact />
          <button
            onClick={onLogout}
            className="text-slate-500 hover:text-red-400 p-2"
            data-testid="fabrication-logout"
            title="Logout"
          >
            <SignOut size={18} />
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <MyTasksBanner includeUnassigned />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <MetricCard label="Total Orders" value={counts.all || 0} tone="cyan" />
          <MetricCard label="Delivered" value={counts.delivered || 0} tone="emerald" />
          <MetricCard label="Advance Received" value={`₹${totalAdvance.toLocaleString("en-IN")}`} tone="amber" />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {STAGES.map((s) => {
            const c = s.key === "all" ? counts.all : counts[s.key];
            const active = stage === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setStage(s.key)}
                data-testid={`fab-stage-${s.key}`}
                className={
                  "shrink-0 px-3 h-9 rounded-sm border font-mono-num text-[11px] tracking-widest transition-colors " +
                  (active
                    ? "border-orange-500 bg-orange-500/20 text-orange-300"
                    : "border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-500")
                }
              >
                {s.label.toUpperCase()}
                {c > 0 && <span className="ml-1.5 text-slate-500">({c})</span>}
              </button>
            );
          })}
        </div>

        {busy ? (
          <div className="surface p-8 rounded-sm text-center text-slate-500">Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="surface p-8 rounded-sm text-center text-slate-500" data-testid="fab-no-orders">
            <Package size={40} className="mx-auto mb-2 text-slate-600" />
            {stage === "all"
              ? "Abhi tak koi order aapko allot nahi hua hai."
              : `"${STAGE_LABEL[stage]}" stage me koi order nahi.`}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((o) => <OrderCard key={o.id} order={o} onOpen={() => onOpen(o.id)} onAction={handleAction} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone }) {
  const toneCls = { cyan: "text-cyan-300", emerald: "text-emerald-400", amber: "text-amber-300" }[tone] || "text-slate-100";
  return (
    <div className="surface p-3 rounded-sm">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-500">{label.toUpperCase()}</div>
      <div className={"font-mono-num font-black text-xl " + toneCls}>{value}</div>
    </div>
  );
}

function OrderCard({ order, onOpen, onAction }) {
  const stageLabel = STAGE_LABEL[order.status] || order.status;
  const isDelivered = order.status === "delivered";
  const sheetCount = (order.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const deadline = order.deadline_date;
  const deadlineInfo = deadline ? (() => {
    const d = new Date(deadline);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const t1 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const t2 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diff = Math.round((t1 - t2) / 86400000);
    const dateLabel = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    return { diff, dateLabel };
  })() : null;
  const canFrameDone = order.status === "new_order" && !order.plate_flow;
  const canFinalReady = order.status === "completed";

  return (
    <div
      className="surface p-4 rounded-sm border-l-4 border-orange-500/60"
      data-testid={`fab-order-card-${order.order_number}`}
    >
      <button
        onClick={onOpen}
        className="block text-left w-full"
        data-testid={`fab-order-open-${order.order_number}`}
      >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono-num text-orange-400 text-lg font-bold">#{order.order_number}</div>
          <div className="text-slate-100 truncate mt-0.5">{order.customer_name}</div>
          <div className="text-slate-400 text-xs font-mono-num mt-0.5 flex items-center gap-1">
            <Phone size={11} /> {order.mobile}
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <span className={
            "shrink-0 text-[9px] font-mono-num tracking-widest px-2 py-0.5 rounded-sm font-bold " +
            (isDelivered ? "bg-emerald-500/15 border border-emerald-500/50 text-emerald-300"
              : "bg-orange-500/15 border border-orange-500/50 text-orange-300")
          }>
            {stageLabel.toUpperCase()}
          </span>
          {!isDelivered && deadlineInfo && (
            <span className={
              "shrink-0 text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm font-bold " +
              (deadlineInfo.diff < 0
                ? "bg-red-500/30 border border-red-500/70 text-red-100 animate-pulse"
                : deadlineInfo.diff === 0
                  ? "bg-red-500/25 border border-red-500/70 text-red-100 animate-pulse"
                  : deadlineInfo.diff <= 2
                    ? "bg-amber-500/25 border border-amber-500/70 text-amber-100"
                    : "bg-cyan-500/15 border border-cyan-500/50 text-cyan-200")
            }>
              🔒 {deadlineInfo.diff < 0
                ? `OVERDUE ${Math.abs(deadlineInfo.diff)}D`
                : deadlineInfo.diff === 0
                  ? "DUE TODAY"
                  : deadlineInfo.diff <= 2
                    ? `DUE ${deadlineInfo.diff}D`
                    : `DUE ${deadlineInfo.dateLabel}`}
            </span>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <StatBox label="ITEMS" value={sheetCount} />
        <StatBox label="SQFT" value={Number(order.total_sqft || 0).toFixed(1)} />
        <StatBox label="ADV" value={`₹${Number(order.advance_from_shobhit || 0).toLocaleString("en-IN")}`} tone="amber" small />
      </div>
      </button>

      {/* Stage-specific action button — only rendered when the fabricator
          actually has something to do at this stage. */}
      {canFrameDone && (
        <button
          onClick={(e) => { e.stopPropagation(); onAction?.(order, "frame_done"); }}
          className="mt-3 w-full h-11 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest text-sm flex items-center justify-center gap-2 transition-colors"
          data-testid={`fab-action-frame-done-${order.order_number}`}
        >
          <CheckCircle size={16} weight="fill" /> FRAME DONE
        </button>
      )}
      {canFinalReady && (
        <div className="mt-3 space-y-2">
          <div className="rounded-sm border border-cyan-500/40 bg-cyan-500/5 px-3 py-2 text-[11px] text-cyan-200">
            📦 Sheets ready — frame me install karo, gate assemble karo, phir Ready for Delivery click karo.
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onAction?.(order, "final_ready"); }}
            className="w-full h-11 rounded-sm bg-cyan-500 hover:bg-cyan-600 text-white font-heading tracking-widest text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid={`fab-action-final-ready-${order.order_number}`}
          >
            <CheckCircle size={16} weight="fill" /> READY FOR DELIVERY
          </button>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, tone = "slate", small = false }) {
  const toneCls = { amber: "text-amber-300", cyan: "text-cyan-300", slate: "text-slate-200" }[tone];
  return (
    <div className="bg-slate-800/50 rounded-sm px-2 py-1.5">
      <div className="text-[9px] font-mono-num tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono-num font-bold ${small ? "text-xs" : "text-sm"} ${toneCls}`}>{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function OrderDetail({ orderId, me, onBack, onLogout }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(true);
  const [acting, setActing] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get(`/fabrication/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  useEffect(() => { setBusy(true); reload(); }, [orderId]);

  const doAction = async (action) => {
    const label = action === "structure_ready"
      ? "Structure ready"
      : action === "fitting_complete"
      ? "Fitting complete"
      : action === "final_ready"
      ? "Final gate ready"
      : "Frame ready";
    if (!window.confirm(`${label} mark karein?`)) return;
    setActing(true);
    try {
      await api.patch(`/fabrication/orders/${orderId}/status`, { action });
      toast.success(
        action === "structure_ready" ? "Structure ready ✓ — size ka photo bhejo"
        : action === "fitting_complete" ? "Fitting complete ✓ — order delivery ke liye ready"
        : action === "final_ready" ? "Delivered ✓"
        : "Frame done → design stage"
      );
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setActing(false); }
  };

  const uploadSizePhoto = async (file, note) => {
    if (!file) return;
    setActing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (note) fd.append("note", note);
      await api.post(`/fabrication/orders/${orderId}/size-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo office ko bhej diya ✓");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setActing(false); }
  };

  const deleteSizePhoto = async (photoId) => {
    if (!window.confirm("Ye photo delete kar dein?")) return;
    try {
      await api.delete(`/fabrication/orders/${orderId}/size-photos/${photoId}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (busy) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 font-mono-num">Loading order…</div>;
  }
  if (!order) return null;

  const timelineStages = order.gate_v2 ? GATE_V2_STAGES.map((g) => ({ key: g.v, label: g.label })) : order.plate_flow ? PLATE_STAGES : STAGES.filter((s) => s.key !== "all");
  const curKey = order.gate_v2 ? ({ in_cutting: "design_cutting" }[order.raw_status] || order.raw_status) : order.plate_flow ? ({ completed: "completed", final_fitting: "completed" }[order.raw_status] || order.raw_status) : order.status;
  const currentStageIdx = timelineStages.findIndex((s) => s.key === curKey);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="fab-order-detail">
      <header className="sticky top-0 z-10 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={onBack} className="text-slate-400 hover:text-white p-1" data-testid="fab-back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono-num tracking-widest text-orange-400">// ORDER</div>
            <div className="font-mono-num text-lg text-orange-400">#{order.order_number}</div>
          </div>
          <button onClick={onLogout} className="text-slate-500 hover:text-red-400 p-2" title="Logout">
            <SignOut size={18} />
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Stage-action banner — surfaces the primary action for whichever
            stage the order is in (structure ready / frame done / final gate ready). */}
        {order.plate_flow && !order.gate_v2 && <CuttingPlatesSection order={order} mode="fab" onChanged={reload} />}
        {order.gate_v2 && <GateSheetSizes order={order} mode="fab" onChanged={reload} />}
        {!order.plate_flow && (order.status === "structure_making" || order.status === "framing_making") && (
          <button
            onClick={() => doAction("structure_ready")}
            disabled={acting}
            className="w-full h-14 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest text-base flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="fab-detail-structure-ready"
          >
            <CheckCircle size={20} weight="fill" /> {acting ? "SAVING…" : "STRUCTURE READY"}
          </button>
        )}
        {!order.plate_flow && order.status === "new_order" && (
          <button
            onClick={() => doAction("frame_done")}
            disabled={acting}
            className="w-full h-14 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest text-base flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="fab-detail-frame-done"
          >
            <CheckCircle size={20} weight="fill" /> {acting ? "SAVING…" : "FRAME DONE — DESIGN STAGE ME BHEJO"}
          </button>
        )}

        {/* Size photo upload — appears after Structure Ready. Fabricator can
            add multiple photos until office confirms. */}
        {!order.plate_flow && (order.status === "structure_ready" || order.status === "size_uploaded") && (
          <SizePhotoUploader
            order={order}
            acting={acting}
            onUpload={uploadSizePhoto}
            onDelete={deleteSizePhoto}
          />
        )}

        {order.status === "size_confirmed" && (
          <div className="surface p-4 rounded-sm border-l-4 border-cyan-500/60 text-cyan-200 text-sm"
               data-testid="fab-size-confirmed-notice">
            <div className="font-heading text-base">✓ OFFICE NE SIZE CONFIRM KAR DIYA</div>
            <div className="text-xs text-cyan-300 mt-1">
              Ab office design taiyaar karega. Aapka current kaam yahan pause hai.
            </div>
          </div>
        )}

        {/* Phase 3: fabricator installs the CNC-cut panels then hits Fitting Complete */}
        {order.status === "final_fitting" && (
          <div className="space-y-2">
            <div className="surface p-4 rounded-sm border-l-4 border-emerald-500/60 text-emerald-200 text-sm"
                 data-testid="fab-cutting-complete-notice">
              <div className="font-heading text-base">✓ CUTTING COMPLETE</div>
              <div className="text-xs text-emerald-300 mt-1">
                CNC panels ready hain. Ab fitting complete karke customer ko delivery ke liye taiyaar rakho.
              </div>
            </div>
            <button
              onClick={() => doAction("fitting_complete")}
              disabled={acting}
              className="w-full h-14 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest text-base flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="fab-detail-fitting-complete"
            >
              <CheckCircle size={20} weight="fill" /> {acting ? "SAVING…" : "FITTING COMPLETE"}
            </button>
          </div>
        )}

        {order.status === "completed" && (
          <button
            onClick={() => doAction("final_ready")}
            disabled={acting}
            className="w-full h-14 rounded-sm bg-cyan-500 hover:bg-cyan-600 text-white font-heading tracking-widest text-base flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="fab-detail-final-ready"
          >
            <CheckCircle size={20} weight="fill" /> {acting ? "SAVING…" : "READY FOR DELIVERY — DELIVER KARO"}
          </button>
        )}
        {order.deadline_date && (() => {
          const d = new Date(order.deadline_date);
          const now = new Date();
          const t1 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
          const t2 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          const diff = Math.round((t1 - t2) / 86400000);
          const dateLabel = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
          const overdue = diff < 0;
          const today = diff === 0;
          const soon = diff > 0 && diff <= 2;
          const cls = overdue || today
            ? "border-red-500/60 bg-red-500/10 text-red-200"
            : soon
              ? "border-amber-500/50 bg-amber-500/10 text-amber-200"
              : "border-cyan-500/40 bg-cyan-500/10 text-cyan-200";
          return (
            <div className={`surface p-4 rounded-sm border-l-4 ${cls}`} data-testid="fab-detail-deadline">
              <div className="text-[10px] font-mono-num tracking-widest opacity-70">🔒 INTERNAL DEADLINE</div>
              <div className="font-heading text-xl font-black mt-0.5">
                {dateLabel}
                <span className="ml-3 text-sm font-normal">
                  {overdue ? `— ${Math.abs(diff)} din OVERDUE`
                    : today ? "— AAJ DELIVER KARNA HAI"
                    : `— ${diff} din baaki`}
                </span>
              </div>
            </div>
          );
        })()}

        {order.deadline_date && (() => {
          const d = new Date(order.deadline_date);
          const now = new Date();
          const t1 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
          const t2 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          const diff = Math.round((t1 - t2) / 86400000);
          const dateLabel = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
          const overdue = diff < 0;
          const today = diff === 0;
          const soon = diff > 0 && diff <= 2;
          const cls = overdue || today
            ? "border-red-500/60 bg-red-500/10 text-red-200"
            : soon
              ? "border-amber-500/50 bg-amber-500/10 text-amber-200"
              : "border-cyan-500/40 bg-cyan-500/10 text-cyan-200";
          return (
            <div className={`surface p-4 rounded-sm border-l-4 ${cls}`} data-testid="fab-detail-deadline">
              <div className="text-[10px] font-mono-num tracking-widest opacity-70">🔒 INTERNAL DEADLINE</div>
              <div className="font-heading text-xl font-black mt-0.5">
                {dateLabel}
                <span className="ml-3 text-sm font-normal">
                  {overdue ? `— ${Math.abs(diff)} din OVERDUE`
                    : today ? "— AAJ DELIVER KARNA HAI"
                    : `— ${diff} din baaki`}
                </span>
              </div>
            </div>
          );
        })()}

        <div className="surface p-4 rounded-sm">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500">CUSTOMER</div>
          <div className="text-lg font-heading font-black mt-0.5">{order.customer_name}</div>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <a href={`tel:${order.mobile}`} className="text-cyan-300 font-mono-num text-sm flex items-center gap-1">
              <Phone size={12} /> {order.mobile}
            </a>
            <a href={`https://wa.me/${waPhone(order.mobile)}`} target="_blank" rel="noreferrer"
               className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1 text-sm">
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </a>
          </div>
          {order.address && (
            <div className="text-slate-400 text-sm flex items-start gap-1 mt-2">
              <MapPin size={12} className="mt-0.5 shrink-0" /> {order.address}
            </div>
          )}
        </div>

        <div className="surface p-4 rounded-sm">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-3">CURRENT STAGE</div>
          <div className="space-y-2">
            {timelineStages.map((s, i) => {
              const done = i <= currentStageIdx;
              const active = i === currentStageIdx;
              return (
                <div key={s.key} className="flex items-center gap-3">
                  {done ? (
                    <CheckCircle size={18} weight="fill" className={active ? "text-orange-400" : "text-emerald-500"} />
                  ) : (
                    <Circle size={18} className="text-slate-600" />
                  )}
                  <span className={
                    "text-sm font-heading tracking-widest " +
                    (active ? "text-orange-300 font-black" : done ? "text-slate-300" : "text-slate-600")
                  }>
                    {s.label.toUpperCase()}
                  </span>
                  {active && (
                    <span className="text-[9px] font-mono-num tracking-widest bg-orange-500/20 border border-orange-500/50 text-orange-300 px-1.5 py-0.5 rounded-sm ml-1 animate-pulse">
                      NOW
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="surface p-4 rounded-sm">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-2">ITEMS / SIZES</div>
          <div className="space-y-2">
            {(order.items || []).map((it, i) => (
              <div key={i} className="border border-slate-700 rounded-sm p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-slate-100 font-heading">{it.item_name || "—"}</div>
                    {it.thickness && <div className="text-slate-400 text-xs font-mono-num">{it.thickness}</div>}
                  </div>
                  <div className="text-right font-mono-num">
                    <div className="text-slate-300">
                      {it.width_in} × {it.height_in} <span className="text-slate-500 text-xs">in</span>
                    </div>
                    <div className="text-cyan-300 text-xs">Qty {it.quantity}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-right text-xs text-slate-400 font-mono-num">
            TOTAL: <span className="text-cyan-300">{order.total_sqft || 0} sqft</span>
          </div>
        </div>

        {(order.design_description || order.notes) && (
          <div className="surface p-4 rounded-sm">
            <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-2">DESIGN NOTES</div>
            <div className="text-sm text-slate-200 whitespace-pre-wrap">
              {order.design_description || order.notes}
            </div>
          </div>
        )}

        {((order.reference_images || []).length > 0 || (order.design_previews || []).length > 0 || order.design_preview) && (
          <div className="surface p-4 rounded-sm">
            <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-2 flex items-center gap-1">
              <ImageSquare size={12} /> DESIGN / REFERENCES
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {(order.design_previews || []).map((u, i) => <PreviewThumb key={"d" + i} url={u} label="DESIGN" />)}
              {order.design_preview && !(order.design_previews || []).length && (
                <PreviewThumb url={order.design_preview} label="DESIGN" />
              )}
              {(order.reference_images || []).map((u, i) => <PreviewThumb key={"r" + i} url={u} label="REF" />)}
            </div>
          </div>
        )}

        <div className="surface p-4 rounded-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono-num tracking-widest text-slate-500 flex items-center gap-1">
              <CurrencyInr size={12} weight="bold" /> ADVANCE RECEIVED (SHOBHIT CREATION → YOU)
            </div>
            <div className="font-mono-num text-amber-300 font-black text-lg">
              ₹{Number(order.advance_total || 0).toLocaleString("en-IN")}
            </div>
          </div>
          {(order.payments || []).length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-3">Abhi tak koi advance nahi mila.</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {(order.payments || []).map((p) => (
                <div key={p.id} className="py-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono-num text-emerald-400 font-bold">
                      + ₹{Number(p.amount).toLocaleString("en-IN")}
                    </div>
                    <div className="text-slate-400 text-xs font-mono-num flex items-center gap-1 mt-0.5">
                      <Clock size={10} /> {p.paid_on || (p.created_at || "").slice(0, 10)} · {p.mode}
                    </div>
                  </div>
                  {p.note && <div className="text-slate-400 text-xs truncate max-w-[45%]">{p.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewThumb({ url, label }) {
  const isImage = /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url);
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block rounded-sm overflow-hidden border border-slate-700 bg-slate-800 aspect-square relative">
      {isImage ? (
        <img src={url} alt={label} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs font-mono-num">{label}</div>
      )}
      <div className="absolute top-1 left-1 text-[9px] font-mono-num tracking-widest bg-slate-950/80 text-cyan-300 px-1 py-0.5 rounded-sm">
        {label}
      </div>
    </a>
  );
}


function SizePhotoUploader({ order, acting, onUpload, onDelete }) {
  const [note, setNote] = useState("");
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("sc_token") : null;
  const photos = order.size_photos || [];

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      await onUpload(f, note);
    }
    setNote("");
    if (e.target) e.target.value = "";
  };

  return (
    <div className="surface p-4 rounded-sm border-l-4 border-amber-500/60" data-testid="fab-size-uploader">
      <div className="text-[10px] font-mono-num tracking-widest text-amber-300 mb-2">
        // UPLOAD CUTTING SIZE
      </div>
      <div className="text-xs text-slate-400 mb-3">
        Structure ready — ab cutting size ka photo camera se leke bhejo. Office cutting size prepare karega.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={acting}
          className="h-14 rounded-sm bg-cyan-500 hover:bg-cyan-600 text-white font-heading tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="fab-size-camera-btn"
        >
          📷 OPEN CAMERA
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={acting}
          className="h-14 rounded-sm bg-slate-700 hover:bg-slate-600 text-white font-heading tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="fab-size-gallery-btn"
        >
          <ImageSquare size={18} weight="fill" /> UPLOAD FROM GALLERY
        </button>
        {/* Hidden inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFile}
          data-testid="fab-size-camera-input"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFile}
          data-testid="fab-size-gallery-input"
        />
      </div>

      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional): left panel / right panel etc"
        className="w-full bg-slate-800/60 border border-slate-600 text-white h-10 rounded-sm px-3 text-sm mb-3"
        data-testid="fab-size-note-input"
      />

      {photos.length > 0 && (
        <>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-1.5">
            UPLOADED PHOTOS ({photos.length})
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {photos.map((p) => (
              <div key={p.id} className="relative border border-slate-700 rounded-sm overflow-hidden aspect-square" data-testid={`fab-size-photo-${p.id}`}>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/size-photos/${p.id}/download?auth=${token || ""}`}
                  alt={p.original_filename || "size"}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => onDelete(p.id)}
                  className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white text-[10px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm"
                  data-testid={`fab-size-photo-del-${p.id}`}
                  disabled={order.status === "size_confirmed"}
                >
                  ✕
                </button>
                {p.note && (
                  <div className="absolute bottom-0 inset-x-0 bg-slate-950/80 text-white text-[10px] px-1.5 py-0.5 truncate">
                    {p.note}
                  </div>
                )}
              </div>
            ))}
          </div>
          {order.status === "size_uploaded" && (
            <div className="mt-3 text-[11px] text-emerald-300 font-mono-num tracking-wider">
              ✓ SIZE OFFICE KO BHEJ DIYA — office ab cutting size prepare karega
            </div>
          )}
        </>
      )}
    </div>
  );
}
